/**
 * The steer catalogue — "suggested next steps" (owner ask 2026-08-04: buttons a
 * recruiter can click to guide the agent WITHOUT interrupting it).
 *
 * Deliberately not the DeterminationComposer. A determination is a decision —
 * it auto-sends, ends screening, and moves the lead's lifecycle. A steer is a
 * nudge: no decision, no hand-off, and the conversation keeps its course.
 *
 * Owner's presentation call: a FIXED row of six, always the same, in the same
 * order — plus the free-text escape hatch. No context ranking to learn.
 *
 * Two shapes:
 *   TIMED   (Offer a call, Send the form link, Ease off, Tell the agent…)
 *           — clicking stages the chip and asks WHEN (owner's answer: "give
 *           the user an option of when to send it"). "On their next reply" is
 *           the non-interrupting default; "Send now" enqueues a turn.
 *   TOGGLE  (Stay on text, Pause follow-ups) — sticky, lit while on, click
 *           again to clear. Nothing is ever sent.
 *
 * Skip this question is TIMED-but-wait-only: it changes the next reply and has
 * no message of its own, so it applies on one click.
 *
 * This list lived inside `SteerBar.tsx` until the standing rail was replaced by
 * the D-5 GuideAgent popover. GuideAgent reuses the owner's fixed seven verbatim
 * — the catalogue is the contract, the presentation is not.
 */

export type SteerKey =
  | 'offer_call' | 'send_form' | 'ease_off' | 'skip_question' | 'note'
  | 'stay_on_text' | 'pause_followups';

export interface SteerDef {
  key: SteerKey;
  label: string;
  /** Hover explainer — same discipline as the determination buttons. */
  tip: string;
  /** 'timed' asks when; 'now' applies on one click; 'toggle' is sticky. */
  shape: 'timed' | 'once' | 'toggle';
  requiresNote?: boolean;
}

/** The owner's six, in the owner's order, then the escape hatch. */
export const STEERS: SteerDef[] = [
  {
    key: 'offer_call',
    label: 'Offer a call',
    shape: 'timed',
    tip: 'The agent offers a quick call with our team and sends the scheduling link. If a call is already on the books it says so instead — it will not hand out a second link.',
  },
  {
    key: 'send_form',
    label: 'Send the form link',
    shape: 'timed',
    tip: 'The agent texts the short online eligibility form. Overrides the usual once-only offer limit, since you asked for it.',
  },
  {
    key: 'stay_on_text',
    label: 'Stay on text',
    shape: 'toggle',
    tip: 'Stops the agent offering the form or a call at all. It works through the screening questions right here, one per message, until you turn this off.',
  },
  {
    key: 'ease_off',
    label: 'Ease off',
    shape: 'timed',
    tip: 'For a lead who sounds hesitant or unsure about us. The next message asks nothing, sends no link, and is simply reassuring — then the agent waits for them.',
  },
  {
    key: 'skip_question',
    label: 'Skip this question',
    shape: 'once',
    tip: 'The agent stops pressing whatever question they are stuck on and moves to the next one. The lead is never told a question went unanswered — a person picks it up.',
  },
  {
    key: 'pause_followups',
    label: 'Pause follow-ups',
    shape: 'toggle',
    tip: 'Stops the nudge ladder. The agent sends nothing on its own but still replies instantly if the lead writes back. Sticky until you turn it off.',
  },
  {
    key: 'note',
    label: 'Tell the agent…',
    shape: 'timed',
    requiresNote: true,
    tip: 'Free-text steering in your own words, followed on the next message. It is guidance, not permission: it can never make the agent state eligibility, ask for medical records, or contradict a determination.',
  },
];
